"use client";

import React from 'react';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { X } from '@/components/icons';
import { cn } from '@/lib/utils';
import useHeaderOffset from '@/hooks/useHeaderOffset';

interface MinimizedNotesStackProps {
  notes: Array<{ id: string; title: string }>;
  onNoteClick: (noteId: string) => void;
  onNoteClose: (noteId: string) => void;
}

export const MinimizedNotesStack: React.FC<MinimizedNotesStackProps> = ({
  notes,
  onNoteClick,
  onNoteClose,
}) => {
  const { minimizedNotes } = useContentSidebar();
  const { bottomNavHeight, isDesktop } = useHeaderOffset();

  const minimizedNoteIds = Object.keys(minimizedNotes).filter((id) => minimizedNotes[id]);
  const minimizedNotesList = notes.filter((n) => minimizedNoteIds.includes(n.id));

  if (minimizedNotesList.length === 0) return null;

  return (
    <>
      {minimizedNotesList.map((note, index) => {
        const bottomOffset = !isDesktop ? bottomNavHeight + index * 64 : 0;

        return (
          <div
            key={note.id}
            className={cn(
              'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg z-[9920] max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
              'transition-all duration-200'
            )}
            style={{
              right: isDesktop ? 'auto' : undefined,
              bottom: !isDesktop ? `${bottomOffset}px` : undefined,
            }}
          >
            <div
              role="button"
              onClick={() => {
                onNoteClick(note.id);
              }}
              className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-muted/50 cursor-pointer"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex-shrink-0" />
                <span className="font-medium truncate text-sm">{note.title || 'Nota'}</span>
              </div>
              <div
                role="button"
                className="h-6 w-6 hover:bg-primary/20 rounded flex items-center justify-center transition-colors flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onNoteClose(note.id);
                }}
              >
                <X className="w-4 h-4" />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
